import type { TelemetryPort } from '@core/domain/ports/driven/TelemetryPort.ts';
import type { NotificationPort } from '../../notifications/domain/ports/NotificationPort.ts';
import { createCorrelationId } from '@core/domain/utils/correlationId.ts';
import { AUTH_SESSION_EXPIRED_EVENT, SHELL_AUTH_LOGIN_SUCCESS_EVENT } from '@core/domain/sessionEvents.ts';
import type { ShellAuthApplicationPort } from '../../app-context/domain/ports/ShellAuthApplicationPort.ts';
import { storeCurrentLocationAsPostLoginReturnUrl } from '../../routing/infrastructure/shellPostLoginReturnUrl.ts';

// --- Public shell routes (no session) ---

export const PUBLIC_SHELL_PATHS: readonly string[] = ['/login'];
/** Normalize path for public check. */
export function normalizePathForPublicCheck(path: string): string {
  if (path.length > 1 && path.endsWith('/')) return path.slice(0, -1);
  return path;
}
/** Whether public shell path. */
export function isPublicShellPath(path: string): boolean {
  const p = normalizePathForPublicCheck(path);
  return PUBLIC_SHELL_PATHS.includes(p);
}

// --- Global listeners (errors, session) ---
let installedWindowErrorHandler: ((ev: ErrorEvent) => void) | null = null;
let installedUnhandledRejectionHandler: ((ev: PromiseRejectionEvent) => void) | null = null;

/**
 * Registers `error` and `unhandledrejection` toward telemetry. Idempotent: replaces previous listeners when reinstalled
 * (useful in tests and HMR). In tests, call {@link resetGlobalErrorTelemetryHandlersForTests} in `afterEach` if needed.
 */
export function installGlobalErrorTelemetryHandlers(telemetry: TelemetryPort, notifications?: NotificationPort): void {
  if (installedWindowErrorHandler) {
    globalThis.removeEventListener('error', installedWindowErrorHandler);
    globalThis.removeEventListener('unhandledrejection', installedUnhandledRejectionHandler!);
  }

  installedWindowErrorHandler = (ev: ErrorEvent): void => {
    try {
      telemetry.logError(ev.message || 'window.error', {
        correlationId: createCorrelationId(),
        source: 'window.error',
        filename: ev.filename,
        lineno: ev.lineno,
        colno: ev.colno,
        error: ev.error,
      });
      notifications?.error('Ha ocurrido un error inesperado. Por favor, intente nuevamente.');
    } catch {
      void 0;
    }
  };

  installedUnhandledRejectionHandler = (ev: PromiseRejectionEvent): void => {
    const reason = ev.reason;
    try {
      telemetry.logError(reason instanceof Error ? reason : String(reason), {
        correlationId: createCorrelationId(),
        source: 'unhandledrejection',
        reasonType: typeof reason,
        stack: reason instanceof Error ? reason.stack : undefined,
      });
      notifications?.error('Ha ocurrido un error inesperado. Por favor, intente nuevamente.');
    } catch {
      void 0;
    }
  };

  globalThis.addEventListener('error', installedWindowErrorHandler);
  globalThis.addEventListener('unhandledrejection', installedUnhandledRejectionHandler);
}

/** Tests only: removes global error listeners. */
export function resetGlobalErrorTelemetryHandlersForTests(): void {
  if (installedWindowErrorHandler) {
    globalThis.removeEventListener('error', installedWindowErrorHandler);
    globalThis.removeEventListener('unhandledrejection', installedUnhandledRejectionHandler!);
    installedWindowErrorHandler = null;
    installedUnhandledRejectionHandler = null;
  }
}

/** Global 401: logout and redirect to /login except on public routes. Returns `removeListener`. */
export function installSessionExpiredListener(
  auth: ShellAuthApplicationPort,
  isPublicPath: (path: string) => boolean
): () => void {
  const handler = (): void => {
    const path = globalThis.location.pathname;
    if (path === '/login') return;
    auth.logout().catch((err) => {
      // On teardown there is no shell composition; minimal trace only.
      // oxlint-disable-next-line no-console -- session-expired logout without strong guarantees
      console.warn('[session] logout tras expiración falló', err);
    });
    if (isPublicPath(path)) return;
    storeCurrentLocationAsPostLoginReturnUrl();
    globalThis.location.replace(`${globalThis.location.origin}/login`);
  };
  globalThis.addEventListener(AUTH_SESSION_EXPIRED_EVENT, handler);
  return () => globalThis.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, handler);
}

/** Notifies the shell that login succeeded in-page (no full reload). */
export function notifyAuthLoginSuccess(): void {
  globalThis.dispatchEvent(new CustomEvent(SHELL_AUTH_LOGIN_SUCCESS_EVENT));
}

/** Listens for in-app login success. Returns `removeListener`. */
export function installAuthLoginSuccessListener(onLoginSuccess: () => void): () => void {
  const handler = (): void => {
    onLoginSuccess();
  };
  globalThis.addEventListener(SHELL_AUTH_LOGIN_SUCCESS_EVENT, handler);
  return () => globalThis.removeEventListener(SHELL_AUTH_LOGIN_SUCCESS_EVENT, handler);
}
